import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";

/** Dotted-triangle Relay mark (the app's SF-symbol logo, in brand pink). */
export function RelayLogo({ size = 72 }: { size?: number }) {
  const dot = (cx: number, cy: number, r: number) => (
    <circle cx={cx} cy={cy} r={r} fill="var(--accent)" key={`${cx}-${cy}`} />
  );
  return (
    <svg className="relay-logo" width={size} height={size} viewBox="0 0 100 100" aria-hidden>
      {dot(28, 30, 12)}
      {dot(72, 30, 12)}
      {dot(50, 72, 12)}
      {dot(40, 27, 3.5)}{dot(50, 26, 3.5)}{dot(60, 27, 3.5)}
      {dot(34, 44, 3.5)}{dot(40, 53, 3.5)}{dot(45, 61, 3.5)}
      {dot(66, 44, 3.5)}{dot(60, 53, 3.5)}{dot(55, 61, 3.5)}
    </svg>
  );
}

/**
 * Relay home — mode picker, mirroring the iOS screen: logo, "Play your way to
 * a match", then 1v1 and Group cards. Auto-enters a live room/duel if the
 * server assigns one (activeFishbowlId / activeDuelId on the user doc).
 */
export default function Relay() {
  const { uid } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(db, "users", uid), async (snap) => {
      const d = snap.data();
      if (d?.activeFishbowlId) {
        // Two guards against bouncing back into a room you just left (which
        // used to ping-pong the router between home and the dead room):
        // 1. cache-only snapshots can carry a stale flag for a beat after
        //    leaving — only auto-enter on server-confirmed data;
        // 2. never re-enter the specific room THIS device already exited
        //    (covers a lingering flag until the sweep clears it).
        if (snap.metadata.fromCache) return;
        if (sessionStorage.getItem("fuse-left-room") === d.activeFishbowlId) return;
        navigate(`/relay/room/${d.activeFishbowlId}`, { replace: true });
      } else if (d?.activeDuelId) {
        // Only re-enter a duel that's still live AND that I haven't decided on
        // yet — otherwise you'd get yanked back in after choosing match/pass
        // while waiting for the other person (activeDuelId lingers until they
        // decide too).
        const duel = (await getDoc(doc(db, "duels", d.activeDuelId as string))).data();
        if (duel && duel.state === "live" && duel.choices?.[uid] === undefined) {
          navigate(`/relay/duel/${d.activeDuelId}`, { replace: true });
        }
      }
    });
  }, [uid, navigate]);

  return (
    <div className="screen">
      <h1>Relay</h1>
      <div className="mode-hero">
        <RelayLogo />
        <h2>Play your way to a match</h2>
        <p>Pick a mode, jump in, and meet someone live.</p>
      </div>

      <button className="mode-card" style={{ marginTop: 52 }} onClick={() => navigate("/relay/duel")}>
        <span className="mode-icon" aria-hidden>👥</span>
        <span>
          <div className="mode-title">1v1</div>
          <div className="mode-sub">Head-to-head with one person</div>
        </span>
        <span className="mode-chevron" aria-hidden>›</span>
      </button>

      <button className="mode-card" onClick={() => navigate("/relay/group")}>
        <span className="mode-icon" aria-hidden>👥👥</span>
        <span>
          <div className="mode-title">Group</div>
          <div className="mode-sub">A room of 8, rotating partners &amp; games</div>
        </span>
        <span className="mode-chevron" aria-hidden>›</span>
      </button>
    </div>
  );
}
